"use client";

// Result sections rendered after the API response.

import { STRENGTH_CONFIG, PLANET_ORDER, type AshtakvargaResponse } from "./AshtakvargaTypes";

function strengthBadge(strength: string) {
  const cfg = STRENGTH_CONFIG[strength as keyof typeof STRENGTH_CONFIG] ?? STRENGTH_CONFIG.average;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

// ── SAV Table ────────────────────────────────────────────────────────────────

export function SAVTable({ result }: { result: AshtakvargaResponse }) {
  return (
    <div>
      <h3
        className="text-[16px] font-bold mb-3"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Sarvashtakvarga (SAV) — All 12 Signs
      </h3>
      <p className="text-[12px] mb-3" style={{ color: "var(--ish-text-faint)" }}>
        Total = {result.sarvashtakvarga_table.reduce((a, b) => a + b, 0)} bindus (always 337 in
        Parashari system)
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {result.sarvashtakvarga_table.map((sav, i) => {
          const strength =
            sav >= 30
              ? "very_strong"
              : sav >= 25
                ? "average"
                : sav >= 20
                  ? "below_average"
                  : "weak";
          const cfg = STRENGTH_CONFIG[strength];
          return (
            <div
              key={i}
              className="rounded-lg p-3 text-center"
              style={{ backgroundColor: cfg.bg, border: "1px solid var(--ish-border)" }}
            >
              <p className="text-[11px] mb-1" style={{ color: "var(--ish-text-faint)" }}>
                {result.sign_names[i]}
              </p>
              <p className="text-[22px] font-bold" style={{ color: cfg.color }}>
                {sav}
              </p>
              <p className="text-[10px] font-semibold mt-0.5" style={{ color: cfg.color }}>
                {cfg.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── BAV Table ────────────────────────────────────────────────────────────────

export function BAVTable({ result }: { result: AshtakvargaResponse }) {
  return (
    <div>
      <h3
        className="text-[16px] font-bold mb-3"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Bhinnashtakvarga (BAV) — Per-Planet Tables
      </h3>
      <p className="text-[12px] mb-3" style={{ color: "var(--ish-text-faint)" }}>
        Bindus each planet contributes to each sign (0–8). 5+ = strong, 2 or fewer = weak.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th
                className="text-left p-2 font-semibold"
                style={{
                  color: "var(--ish-dark)",
                  borderBottom: "1px solid var(--ish-border)",
                }}
              >
                Planet
              </th>
              {result.sign_names.map((sign, i) => (
                <th
                  key={i}
                  className="p-2 text-center font-semibold"
                  style={{
                    color: "var(--ish-text-muted)",
                    borderBottom: "1px solid var(--ish-border)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sign.split(" ")[0]}
                </th>
              ))}
              <th
                className="p-2 text-center font-semibold"
                style={{
                  color: "var(--ish-dark)",
                  borderBottom: "1px solid var(--ish-border)",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {PLANET_ORDER.filter((p) => p in result.planet_ashtakvarga_tables).map((planet) => {
              const row = result.planet_ashtakvarga_tables[planet];
              const total = result.total_bindus[planet] ?? 0;
              const strength = result.planet_strength[planet] ?? "moderate";
              return (
                <tr key={planet} style={{ borderBottom: "1px solid var(--ish-border)" }}>
                  <td className="p-2 font-semibold" style={{ color: "var(--ish-dark)" }}>
                    {result.planet_display_en[planet] ?? planet}
                  </td>
                  {row.map((v, i) => (
                    <td
                      key={i}
                      className="p-2 text-center"
                      style={{
                        color:
                          v >= 5
                            ? "var(--color-success, #2a7a4b)"
                            : v <= 2
                              ? "#c0392b"
                              : "var(--ish-text-muted)",
                        fontWeight: v >= 5 || v <= 2 ? 700 : 400,
                      }}
                    >
                      {v}
                    </td>
                  ))}
                  <td className="p-2 text-center">
                    <span
                      className="inline-block px-2 py-0.5 rounded text-[11px] font-bold"
                      style={{
                        color:
                          strength === "strong"
                            ? "var(--color-success, #2a7a4b)"
                            : strength === "weak"
                              ? "#c0392b"
                              : "var(--ish-accent)",
                        backgroundColor:
                          strength === "strong"
                            ? "rgba(42,122,75,0.08)"
                            : strength === "weak"
                              ? "rgba(192,57,43,0.08)"
                              : "var(--ish-accent-light)",
                      }}
                    >
                      {total}
                    </span>
                  </td>
                </tr>
              );
            })}
            {/* SAV total row */}
            <tr style={{ backgroundColor: "var(--ish-accent-light)" }}>
              <td className="p-2 font-bold" style={{ color: "var(--ish-accent)" }}>
                SAV Total
              </td>
              {result.sarvashtakvarga_table.map((v, i) => (
                <td
                  key={i}
                  className="p-2 text-center font-bold"
                  style={{ color: "var(--ish-accent)" }}
                >
                  {v}
                </td>
              ))}
              <td className="p-2 text-center font-bold" style={{ color: "var(--ish-accent)" }}>
                337
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── House Predictions ────────────────────────────────────────────────────────

export function HousePredictions({ result }: { result: AshtakvargaResponse }) {
  return (
    <div>
      <h3
        className="text-[16px] font-bold mb-3"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        House-by-House Predictions
      </h3>
      <p className="text-[12px] mb-4" style={{ color: "var(--ish-text-faint)" }}>
        SAV ordered by house from your lagna (ascendant).
      </p>
      <div className="space-y-3">
        {result.predictions.map((pred) => {
          const cfg =
            STRENGTH_CONFIG[pred.strength as keyof typeof STRENGTH_CONFIG] ??
            STRENGTH_CONFIG.average;
          return (
            <div
              key={pred.house}
              className="rounded-lg p-4"
              style={{ border: "1px solid var(--ish-border)", backgroundColor: cfg.bg }}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
                  House {pred.house}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold" style={{ color: cfg.color }}>
                    {pred.sav}
                  </span>
                  {strengthBadge(pred.strength)}
                </div>
              </div>
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
                {pred.summary}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
